<template>
    <div class="opportunity-status-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Opportunity Stage</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div class="form-wrapper" v-else>
                    <div class="form-row">
                        <div class="input-container">
                            <app-input
                                v-model="opportunityStatus.name"
                                :label="'Name'"
                                placeholder="Name"
                                :isValid="!submitted || $v.opportunityStatus.name.required"
                                :error="`Name is required`"
                            />
                        </div>
                        <div class="input-container">
                            <status-color-dropdown v-model="opportunityStatus.color" :label="'Color'"/>
                        </div>        
                    </div>
                    <div class="input-container">
                        <p class="opportunity-status-label">Status</p>
                        <app-input-radio v-model="opportunityStatus.isWon" :options="statusOptions" />
                    </div>
                </div>
            </div> 
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const StatusColorDropdown = () => import('@/components/common/status-color-dropdown');
const AppInputRadio = () => import('@/components/common/app-input-radio');

import EventBus from '@/scripts/event-bus.js';
import { required} from 'vuelidate/lib/validators';

export default {
    name: 'opportunity-status-widget',

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'loading-screen-svg': LoadingScreenSvg,
        'status-color-dropdown': StatusColorDropdown,
        'app-input-radio': AppInputRadio,
    },

    data() {
        return {
            submitted: false,
            isLoading: true,
            opportunityStatus: {
                id: this.widget.opportunityStatusId,
                clientId: this.widget.clientId,
                name: null,
                color: 'Grey',
                isWon: null
            },
            statusOptions: [{ 
                name: 'Won',
                value: 'Won'
            }, {
                name: 'In Progress',
                value: 'InProgress'
            }, {
                name: 'Lost',
                value: 'Lost'
            }],
        }
    },

     validations: {
        opportunityStatus: {
            name: {
                required
            },
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if(this.opportunityStatus.id) {
                this.patch();
            } 
            else {
                this.post();
            }
        },

        patch() {
            this.isLoading = true;

            var changes = [{
                'name': 'Name',
                'value': this.opportunityStatus.name
            }, {
                'name': 'Color',
                'value': this.opportunityStatus.color
            }, {
                'name': 'IsWon',
                'value': this.getBooleanByStatusValue(this.opportunityStatus.isWon.value)
            }];

            this.$api
                .patch(`/api/DealStatuses/${ this.opportunityStatus.id }`, changes)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your opportunity stage has been successfully updated.');
                    EventBus.$emit('opportunity-status:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while updating your opportunity stage.', { error });
                });
        },

        post() {
            this.isLoading = true;

            var opportunityStatus = {
                Name: this.opportunityStatus.name,
                Color: this.opportunityStatus.color,
                ClientId: this.opportunityStatus.clientId,
                IsWon: this.getBooleanByStatusValue(this.opportunityStatus.isWon.value)
            };

            this.$api
                .post(`/api/DealStatuses`, opportunityStatus)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your opportunity stage has been successfully created.');
                    EventBus.$emit('opportunity-status:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while creating your opportunity stage.', { error });
                });
        },
        
        getStatusValueByBoolean(isWon) {
            switch(isWon) {
                case true:
                    return this.statusOptions[0];
                    break;
                case false:
                    return this.statusOptions[2];
                    break;
                case null:
                    return this.statusOptions[1];
                    break;
            }
        },

        getBooleanByStatusValue(statusValue) {
            switch(statusValue) {
                case 'Won':
                    return true;
                    break;
                case 'Lost':
                    return false;
                    break;
                case 'InProgress':
                    return null;
                    break;
            }
        },

        getOpportunityStatus() {
            this.isLoading = true;

            this.$api
                .get(`/service/DealStatuses?$filter=Id eq ${ this.opportunityStatus.id }`)
                .then(response => {
                    this.opportunityStatus.id = response.data.value[0].Id;
                    this.opportunityStatus.name = response.data.value[0].Name;
                    this.opportunityStatus.color = response.data.value[0].Color;
                    this.opportunityStatus.isWon = this.getStatusValueByBoolean(response.data.value[0].IsWon);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your opportunity stage.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },

    created() {
        if (this.opportunityStatus.id) {
            this.getOpportunityStatus();
        }
        else {
            this.opportunityStatus.isWon = this.statusOptions[1];
            this.isLoading = false;
        }
    }
}
</script>

<style>
.opportunity-status-widget .form-row {
    display: flex;
    flex-direction: column;
}

.opportunity-status-widget .app-input {
    margin-bottom: 16px;
}

.opportunity-status-widget .input-container .status-color-dropdown {
    margin-bottom: 16px;
}

.opportunity-status-widget .app-input-radio {
    display: flex;
    justify-content: space-between;
    width: 350px;
}

.opportunity-status-widget .app-input-radio .circle {
    border-color: #CAC6CB;
}

.opportunity-status-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.opportunity-status-widget .widget-contents-container .transparent-btn:hover {
    background: #E1E0E5;
}

.opportunity-status-widget .app-input input {
    padding: 8px 15px;
}

.opportunity-status-widget .status-color-dropdown {
    max-width: 100px;
    margin-right: 10px;
}

.opportunity-status-widget .opportunity-status-label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
}
</style>